import {
	Alert,
	Box,
	Button,
	Col,
	Divider,
	Grid,
	Heading,
	Icons,
	Inline,
	Spinner,
	Stack,
	Text,
	useId,
} from "@sembark-travel/ui-base"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import {
	Form,
	SubmissionError,
	TextAreaInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import * as Validator from "yup"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useXHR } from "@sembark-travel/xhr"
import useSWR from "swr"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import { ReorderSections } from "./ReorderSections"
import { TTripQuoteWhatsAppTemplate } from "./store"

export function TripQuoteWhatsAppTemplates() {
	const xhr = useXHR()
	const { hasPermission } = useCheckPermissions()
	const [isDialogOpen, openDialog, closeDialog] = useDialog()
	const { data, mutate, error } = useSWR("/trip-quote-whatsapp-templates", () =>
		xhr
			.get<{
				data: TTripQuoteWhatsAppTemplate
			}>("/trip-quote-whatsapp-templates/create")
			.then((resp) => resp.data.data)
	)
	if (!data) {
		if (error) {
			return (
				<Alert status="error">
					{error.message || "Something went wrong. Please try after sometime."}
				</Alert>
			)
		}
		return <Spinner alignCenter padding="4" />
	}
	return (
		<Stack gap="4">
			<Inline justifyContent="between" gap="4" alignItems="center">
				<Inline gap="4" alignItems="center">
					<Inline
						size="12"
						rounded="full"
						bgColor="default"
						borderWidth="1"
						alignItems="center"
						justifyContent="center"
						color="muted"
					>
						<Icons.Phone size="6" />
					</Inline>
					<Stack gap="1">
						<Heading as="h3">Quote WhatsApp Template Configurations</Heading>
						<Text>
							These configurations will be applied to quotes shared over
							WhatsApp only.
						</Text>
					</Stack>
				</Inline>
				{hasPermission(PERMISSIONS.MANAGE_USERS) ? (
					<Box>
						<Button onClick={openDialog} status="primary" size="sm">
							<Icons.Pencil /> Edit
						</Button>
					</Box>
				) : null}
			</Inline>
			<Divider marginY="0" />
			<Stack gap="4">
				<Stack gap="1">
					<Text fontWeight="semibold">Greetings:</Text>
					<Box
						paddingX="4"
						paddingY="2"
						borderWidth="1"
						rounded="md"
						bgColor="default"
						style={{ whiteSpace: "pre-wrap" }}
					>
						<Text>{data.greetings}</Text>
					</Box>
					<Text fontSize="sm" color="muted">
						A confirmed quote, and one reworked after conversion, open with
						their own wording.
					</Text>
				</Stack>
				<Stack gap="1">
					<Text fontWeight="semibold">
						<Icons.SwitchHorizontal rotate="90" /> Sections Ordering
					</Text>
					<Stack
						gap="4"
						padding="4"
						rounded="md"
						bgColor="default"
						borderWidth="1"
					>
						<Stack gap="2">
							{data.section_ordering_settings?.map((section, index) => (
								<Inline key={section.section} gap="2" alignItems="center">
									<Stack
										size="6"
										alignItems="center"
										justifyContent="center"
										borderWidth="1"
										rounded="full"
										bgColor="success_emphasis"
										color="on_emphasis"
									>
										<Text fontWeight="semibold">{index + 1}</Text>
									</Stack>
									<Box flex="1" minWidth="0">
										<Box
											paddingX="4"
											paddingY="2"
											borderWidth="1"
											rounded="md"
											maxWidth="xs"
										>
											<Text>{section.title}</Text>
										</Box>
									</Box>
								</Inline>
							))}
						</Stack>
					</Stack>
				</Stack>
			</Stack>
			<Dialog
				open={isDialogOpen}
				onClose={closeDialog}
				title="Update Quote WhatsApp Template"
				lg
			>
				<Dialog.Body>
					<EditTripQuoteWhatsAppTemplate
						initialValues={data}
						onCancel={closeDialog}
						onSuccess={() => {
							closeDialog()
							mutate()
						}}
					/>
				</Dialog.Body>
			</Dialog>
		</Stack>
	)
}

function EditTripQuoteWhatsAppTemplate({
	initialValues,
	onCancel,
	onSuccess,
}: {
	initialValues: TTripQuoteWhatsAppTemplate
	onCancel: () => void
	onSuccess: () => void
}) {
	const xhr = useXHR()
	const id__ = useId()
	return (
		<Form<TTripQuoteWhatsAppTemplate>
			initialValues={initialValues}
			validate={validateFormValues(
				Validator.object().shape({
					greetings: Validator.string().required("Greetings are required"),
				})
			)}
			subscription={{ submitting: true, values: true }}
			onSubmit={withServerErrors(async (values) => {
				await xhr.post("/trip-quote-whatsapp-templates", {
					...values,
					// the order is the list's own, so it is renumbered from
					// wherever the re-ordering left each section
					section_ordering_settings: (
						values.section_ordering_settings || []
					).map((section, index) => ({
						section: section.section,
						order: index + 1,
					})),
				})
				showSnackbar("Quote WhatsApp template updated successfully.")
				onSuccess()
			})}
		>
			{({ submitting, handleSubmit, form, values }) => (
				<form noValidate onSubmit={handleSubmit} id={id__}>
					<Stack gap="4">
						<Grid gap="6">
							<Col sm={12} md={4}>
								<Stack gap="1">
									<Heading fontSize="md">Template for Greetings</Heading>
									<Text color="muted">
										Customize the greetings message according to your needs. A
										confirmed quote, and one reworked after conversion, open
										with their own wording.
									</Text>
								</Stack>
							</Col>
							<Col>
								<Stack gap="4">
									<TextAreaInputField
										name="greetings"
										label="Greetings"
										secondaryLabel="required"
										rows={5}
										help={`Use ${(initialValues.variables || [])
											.map((v) => v.placeholder)
											.join(", ")} to name the guest and your brand.`}
									/>
								</Stack>
							</Col>
						</Grid>

						<Divider marginY="0" />

						<Grid gap="6">
							<Col sm={12} md={4}>
								<Stack gap="1">
									<Heading fontSize="md">Re-Order Sections</Heading>
									<Text color="muted">
										You can change the ordering of sections in the message such
										as Hotels, Total Price and Cancellation Policy. A section
										with nothing to say is left out.
									</Text>
								</Stack>
							</Col>
							<Col>
								<Box flex="1">
									<Stack gap="4">
										{(values.section_ordering_settings || []).map(
											(section, index) => (
												<Inline
													key={section.section}
													gap="2"
													alignItems="center"
												>
													<Stack
														size="6"
														alignItems="center"
														justifyContent="center"
														borderWidth="1"
														rounded="full"
														bgColor="success_emphasis"
														color="on_emphasis"
													>
														<Text fontWeight="semibold">{index + 1}</Text>
													</Stack>
													<Box flex="1" minWidth="0">
														<Box
															paddingX="4"
															paddingY="2"
															borderWidth="1"
															rounded="md"
															maxWidth="xs"
														>
															<Text>{section.title}</Text>
														</Box>
													</Box>
												</Inline>
											)
										)}
										<Box flex="1" paddingLeft="8">
											<ReorderSections
												sections={values.section_ordering_settings || []}
												onSuccess={(value) => {
													form.change("section_ordering_settings", value)
												}}
											>
												{({ reOrder }) => (
													<Button status="primary" onClick={reOrder}>
														<Icons.SwitchHorizontal rotate="90" />
														Re-Order
													</Button>
												)}
											</ReorderSections>
										</Box>
									</Stack>
								</Box>
							</Col>
						</Grid>

						<Grid gap="0">
							<Col sm={12} md={{ offset: 4, span: 8 }}>
								<Stack gap="4" paddingLeft={{ md: "6" }}>
									<SubmissionError />
									<Inline gap="4">
										<Button type="submit" disabled={submitting}>
											{submitting ? "Please wait..." : "Save Template"}
										</Button>
										<Button
											disabled={submitting}
											onClick={() => onCancel()}
											level="tertiary"
										>
											Cancel
										</Button>
									</Inline>
								</Stack>
							</Col>
						</Grid>
					</Stack>
				</form>
			)}
		</Form>
	)
}
